import React, { useRef, useState } from "react";

import { format } from "@gamedev-web/gamedev-sdk-core";
import { HubAccessLevel, HubInvitationStatus } from "@gamedev-web/one-gaming-crm";
import { Dropdown, Icon, IconSize } from "@gaming/xds-react-js";
import { MoreVertical } from "@gaming/xds-react-js-icons";

import { PageLoadingSpinner } from "../../../../components/PageLoadingSpinner";
import { LoadingSpinner } from "../../../../components/Spinner";
import { formatShortDate } from "../../../../helpers/dateHelpers";
import { useStrings } from "../../../../localization/localizationHelpers";
import type { AdminInvitation, AdminInvitationStatus } from "../../../../models/adminPage";
import { getFuzzySearchScore } from "../../fuzzySearch";
import {
  type AdminActionConfirmation,
  AdminActionConfirmationModal,
} from "../AdminActionConfirmationModal";
import { AdminMultiSelectDropdown, AdminSelectDropdown } from "../AdminDropdown";
import { AdminTableToolbar } from "../AdminTableToolbar";
import * as styles from "../../AdminPage.module.css";

const allInvitersFilter = "__all__";
type SortDirection = "ascending" | "descending";

type InvitationSortKey = "firstName" | "accessLevel" | "createdOn" | "invitedBy" | "status";

type PendingInvitationsPanelProps = Readonly<{
  error: boolean;
  invitations: AdminInvitation[] | null;
  isLoading: boolean;
  isVisible: boolean;
  onRefresh: () => void;
  onEditInvitation: (invitation: AdminInvitation) => void;
  onRevokeInvitation: (invitationId: string) => Promise<void>;
  onResendInvitation: (invitation: AdminInvitation) => Promise<void>;
  resendingInvitationIds: ReadonlySet<string>;
  revokingInvitationIds: ReadonlySet<string>;
}>;

type InvitationActionLabels = Readonly<{
  moreOptions: string;
  resend: string;
  revoke: string;
}>;

const InvitationActionMenu = ({
  invitation,
  labels,
  onRevokeInvitation,
  onResendInvitation,
  resendingInvitationIds,
  revokingInvitationIds,
}: Readonly<{
  invitation: AdminInvitation;
  labels: InvitationActionLabels;
  onRevokeInvitation: (invitationId: string) => void;
  onResendInvitation: (invitation: AdminInvitation) => void;
  resendingInvitationIds: ReadonlySet<string>;
  revokingInvitationIds: ReadonlySet<string>;
}>) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isPending = invitation.status === HubInvitationStatus.Pending;
  const isBusy = (isPending ? revokingInvitationIds : resendingInvitationIds).has(
    invitation.invitationId
  );

  return (
    <div className={styles.actionMenu}>
      <button
        ref={triggerRef}
        className={styles.actionMenuSummary}
        type="button"
        aria-label={labels.moreOptions}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={isBusy}
        onClick={() => setIsOpen(open => !open)}
      >
        {isBusy ? (
          <span className={styles.cardSpinner}>
            <LoadingSpinner />
          </span>
        ) : (
          <Icon aria-hidden="true" icon={MoreVertical} iconSize={IconSize.XSmall} />
        )}
      </button>
      {isOpen && (
        <Dropdown
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          relativeRef={triggerRef as React.RefObject<HTMLElement>}
          horizontalAlignment="right"
          backgroundDismissable
          dimBackground="none"
          className={styles.actionMenuPopup}
          zIndex={1000}
        >
          <div role="menu">
            <button
              className={styles.actionMenuItem}
              role="menuitem"
              type="button"
              onClick={() => {
                setIsOpen(false);
                if (isPending) {
                  onRevokeInvitation(invitation.invitationId);
                } else {
                  onResendInvitation(invitation);
                }
              }}
            >
              {isPending ? labels.revoke : labels.resend}
            </button>
          </div>
        </Dropdown>
      )}
    </div>
  );
};

export const PendingInvitationsPanel = ({
  error,
  invitations,
  isLoading,
  isVisible,
  onRefresh,
  onEditInvitation,
  onRevokeInvitation,
  onResendInvitation,
  resendingInvitationIds,
  revokingInvitationIds,
}: PendingInvitationsPanelProps) => {
  const [
    pendingMembersTitle,
    pendingMembersEmptyLabel,
    pendingMembersLoadingLabel,
    pendingMembersErrorLabel,
    inviteeLabel,
    gameConceptAdminLabel,
    gameManagerLabel,
    submittedConceptsOnlyLabel,
    sentLabel,
    sentByLabel,
    activeUntilFormat,
    actionsLabel,
    revokeInvitationLabel,
    searchLabel,
    filterAccessLevelLabel,
    allLabel,
    noResultsLabel,
    closeSearchLabel,
    refreshLabel,
    statusLabel,
    filterInvitedByLabel,
    filterStatusLabel,
    pendingStatusLabel,
    rejectedStatusLabel,
    revokedStatusLabel,
    expiredStatusLabel,
    resendInvitationLabel,
    sortAscendingLabel,
    sortDescendingLabel,
    moreOptionsLabel,
    roleFilterLabel,
  ] = useStrings(
    "partnerAdminPendingMembersTitle",
    "partnerAdminPendingMembersEmptyLabel",
    "partnerAdminLoadingLabel",
    "partnerAdminErrorLabel",
    "partnerAdminInvitationInviteeLabel",
    "partnerAdminGameConceptAdminAccessLabel",
    "partnerAdminGameManagerAccessLabel",
    "partnerAdminSubmittedConceptsOnlyAccessLabel",
    "partnerAdminInvitationSentLabel",
    "partnerAdminInvitationSentByLabel",
    "partnerAdminInvitationActiveUntilFormat",
    "partnerAdminActionsLabel",
    "partnerAdminRevokeInvitationLabel",
    "partnerAdminMemberSearchLabel",
    "partnerAdminFilterAccessLevelLabel",
    "all",
    "noResultsFound",
    "closeSearchAriaLabel",
    "partnerAdminRefreshLabel",
    "partnerAdminInvitationStatusLabel",
    "partnerAdminFilterInvitedByLabel",
    "partnerAdminFilterInvitationStatusLabel",
    "partnerAdminInvitationPendingStatus",
    "partnerAdminInvitationRejectedStatus",
    "partnerAdminInvitationRevokedStatus",
    "partnerAdminInvitationExpiredStatus",
    "partnerAdminResendInvitationLabel",
    "partnerAdminSortAscendingLabel",
    "partnerAdminSortDescendingLabel",
    "moreOptionsAriaLabel",
    "partnerAdminRoleFilterLabel"
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [accessFilters, setAccessFilters] = useState<string[]>([]);
  const [inviterFilter, setInviterFilter] = useState(allInvitersFilter);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<InvitationSortKey>("firstName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("ascending");
  const [confirmation, setConfirmation] = useState<AdminActionConfirmation | null>(null);
  const accessLevelLabels: Record<HubAccessLevel, string> = {
    [HubAccessLevel.Admin]: gameConceptAdminLabel,
    [HubAccessLevel.GameManager]: gameManagerLabel,
    [HubAccessLevel.User]: submittedConceptsOnlyLabel,
  };
  const statusNames: Record<AdminInvitationStatus, string> = {
    [HubInvitationStatus.Pending]: pendingStatusLabel,
    [HubInvitationStatus.Rejected]: rejectedStatusLabel,
    [HubInvitationStatus.Revoked]: revokedStatusLabel,
    [HubInvitationStatus.Expired]: expiredStatusLabel,
  };
  const getInvitationStatusLabel = (invitation: AdminInvitation) =>
    invitation.status === HubInvitationStatus.Pending && invitation.expiresOn
      ? format(activeUntilFormat, formatShortDate(invitation.expiresOn))
      : statusNames[invitation.status];
  const actionLabels = {
    moreOptions: moreOptionsLabel,
    resend: resendInvitationLabel,
    revoke: revokeInvitationLabel,
  };
  const accessFilterOptions = [
    { id: String(HubAccessLevel.Admin), label: gameConceptAdminLabel },
    { id: String(HubAccessLevel.GameManager), label: gameManagerLabel },
    { id: String(HubAccessLevel.User), label: submittedConceptsOnlyLabel },
  ];
  const inviterFilterOptions = [
    { id: allInvitersFilter, label: allLabel },
    ...Array.from(
      new Set((invitations ?? []).map(invitation => invitation.invitedBy).filter(Boolean))
    )
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }))
      .map(inviter => ({ id: inviter, label: inviter })),
  ];
  const statusFilterOptions = [
    { id: String(HubInvitationStatus.Pending), label: pendingStatusLabel },
    { id: String(HubInvitationStatus.Rejected), label: rejectedStatusLabel },
    { id: String(HubInvitationStatus.Revoked), label: revokedStatusLabel },
    { id: String(HubInvitationStatus.Expired), label: expiredStatusLabel },
  ];
  const hasSearch = searchQuery.trim().length > 0;
  const filteredInvitations = (invitations ?? [])
    .map(invitation => ({
      invitation,
      searchScore: getFuzzySearchScore(searchQuery, [
        invitation.firstName,
        invitation.lastName,
        `${invitation.firstName} ${invitation.lastName}`,
        invitation.jobTitle,
        invitation.email,
        invitation.invitedBy,
        accessLevelLabels[invitation.accessLevel],
        statusNames[invitation.status],
      ]),
    }))
    .filter(
      ({ invitation, searchScore }) =>
        searchScore !== undefined &&
        (accessFilters.length === 0 || accessFilters.includes(String(invitation.accessLevel))) &&
        (inviterFilter === allInvitersFilter || invitation.invitedBy === inviterFilter) &&
        (statusFilters.length === 0 || statusFilters.includes(String(invitation.status)))
    );
  const getSortValue = (invitation: AdminInvitation) =>
    sortKey === "accessLevel"
      ? accessLevelLabels[invitation.accessLevel]
      : sortKey === "status"
      ? getInvitationStatusLabel(invitation)
      : invitation[sortKey];
  const sortedInvitations = filteredInvitations
    .sort((left, right) => {
      if (hasSearch && left.searchScore !== right.searchScore) {
        return (left.searchScore ?? 0) - (right.searchScore ?? 0);
      }
      const comparison = getSortValue(left.invitation).localeCompare(
        getSortValue(right.invitation),
        undefined,
        {
          sensitivity: "base",
        }
      );
      return sortDirection === "ascending" ? comparison : -comparison;
    })
    .map(({ invitation }) => invitation);
  const renderSortableHeader = (label: string, key: InvitationSortKey) => {
    const isActive = sortKey === key;
    const nextDirectionLabel =
      isActive && sortDirection === "ascending" ? sortDescendingLabel : sortAscendingLabel;

    return (
      <th scope="col" aria-sort={isActive ? sortDirection : "none"}>
        <button
          className={styles.invitationSortButton}
          type="button"
          aria-label={`${label}: ${nextDirectionLabel}`}
          onClick={() => {
            if (isActive) {
              setSortDirection(current => (current === "ascending" ? "descending" : "ascending"));
            } else {
              setSortKey(key);
              setSortDirection("ascending");
            }
          }}
        >
          {label}
          <span className={styles.invitationSortIndicator} aria-hidden="true">
            <span
              className={`${styles.invitationSortCaret} ${styles.invitationSortCaretUp} ${
                isActive && sortDirection === "ascending" ? styles.invitationSortCaretActive : ""
              }`}
            />
            <span
              className={`${styles.invitationSortCaret} ${styles.invitationSortCaretDown} ${
                isActive && sortDirection === "descending" ? styles.invitationSortCaretActive : ""
              }`}
            />
          </span>
        </button>
      </th>
    );
  };

  if (!isVisible) {
    return null;
  }

  return (
    <section role="tabpanel" aria-label={pendingMembersTitle}>
      <AdminTableToolbar
        hasActiveFilters={
          hasSearch ||
          accessFilters.length > 0 ||
          inviterFilter !== allInvitersFilter ||
          statusFilters.length > 0
        }
        closeSearchLabel={closeSearchLabel}
        isRefreshing={isLoading}
        onClear={() => {
          setSearchQuery("");
          setAccessFilters([]);
          setInviterFilter(allInvitersFilter);
          setStatusFilters([]);
        }}
        onRefresh={onRefresh}
        onSearchChange={setSearchQuery}
        refreshLabel={refreshLabel}
        resultCount={sortedInvitations.length}
        searchLabel={searchLabel}
        searchQuery={searchQuery}
      >
        <AdminMultiSelectDropdown
          ariaLabel={filterAccessLevelLabel}
          label={roleFilterLabel}
          options={accessFilterOptions}
          values={accessFilters}
          onChange={setAccessFilters}
        />
        <AdminSelectDropdown
          ariaLabel={filterInvitedByLabel}
          label={sentByLabel}
          options={inviterFilterOptions}
          value={inviterFilter}
          onChange={setInviterFilter}
        />
        <AdminMultiSelectDropdown
          ariaLabel={filterStatusLabel}
          label={statusLabel}
          options={statusFilterOptions}
          values={statusFilters}
          onChange={setStatusFilters}
        />
      </AdminTableToolbar>
      {isLoading || (invitations === null && !error) ? (
        <div
          role="status"
          aria-live="polite"
          aria-label={pendingMembersLoadingLabel}
          aria-busy="true"
        >
          <PageLoadingSpinner />
        </div>
      ) : error ? (
        <p className={styles.message} role="alert">
          {pendingMembersErrorLabel}
        </p>
      ) : invitations?.length === 0 ? (
        <p className={styles.message}>{pendingMembersEmptyLabel}</p>
      ) : sortedInvitations.length === 0 ? (
        <p className={styles.message}>{noResultsLabel}</p>
      ) : (
        <div className={styles.invitationTableContainer}>
          <table className={styles.invitationTable} aria-label={pendingMembersTitle}>
            <thead>
              <tr>
                {renderSortableHeader(inviteeLabel, "firstName")}
                {renderSortableHeader(roleFilterLabel, "accessLevel")}
                {renderSortableHeader(sentLabel, "createdOn")}
                {renderSortableHeader(sentByLabel, "invitedBy")}
                {renderSortableHeader(statusLabel, "status")}
                <th className={styles.invitationActionsHeader} scope="col">
                  {actionsLabel}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedInvitations.map(invitation => (
                <tr key={invitation.invitationId}>
                  <td>
                    <div className={styles.invitationIdentity}>
                      <strong>
                        <span>{invitation.firstName}</span> <span>{invitation.lastName}</span>
                      </strong>
                      <span>{invitation.email}</span>
                    </div>
                  </td>
                  <td>{accessLevelLabels[invitation.accessLevel]}</td>
                  <td>
                    {invitation.createdOn && (
                      <time dateTime={invitation.createdOn}>
                        {formatShortDate(invitation.createdOn)}
                      </time>
                    )}
                  </td>
                  <td>{invitation.invitedBy}</td>
                  <td>
                    {invitation.status === HubInvitationStatus.Pending && invitation.expiresOn ? (
                      <time dateTime={invitation.expiresOn}>
                        {getInvitationStatusLabel(invitation)}
                      </time>
                    ) : (
                      getInvitationStatusLabel(invitation)
                    )}
                  </td>
                  <td className={styles.invitationActions}>
                    <InvitationActionMenu
                      invitation={invitation}
                      labels={actionLabels}
                      onRevokeInvitation={() =>
                        setConfirmation({ action: "revokeInvitation", member: invitation })
                      }
                      onResendInvitation={() =>
                        setConfirmation({ action: "resendInvitation", member: invitation })
                      }
                      resendingInvitationIds={resendingInvitationIds}
                      revokingInvitationIds={revokingInvitationIds}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AdminActionConfirmationModal
        confirmation={confirmation}
        isBusy={
          (confirmation?.action === "resendInvitation" &&
            resendingInvitationIds.has(confirmation.member.invitationId)) ||
          (confirmation?.action === "revokeInvitation" &&
            revokingInvitationIds.has(confirmation.member.invitationId))
        }
        onCancel={() => setConfirmation(null)}
        onEditInvitation={invitation => {
          setConfirmation(null);
          onEditInvitation(invitation);
        }}
        onConfirm={async selected => {
          if (selected.action === "resendInvitation") {
            await onResendInvitation(selected.member);
            setConfirmation(null);
            return;
          }
          if (selected.action !== "revokeInvitation") {
            return;
          }
          await onRevokeInvitation(selected.member.invitationId);
          setConfirmation(null);
        }}
      />
    </section>
  );
};
